import { type CSSProperties, useEffect, useRef } from 'react'

interface EmailPreviewFrameProps {
    /** Already sanitized email HTML. */
    html: string
    title: string
    className?: string
    style?: CSSProperties
    /** Inline style for the preview document's body — its padding, base font and colours. */
    bodyStyle?: string
    /** Shrink the email to fit the frame's width, never past this fraction of its size. A mail client shows a
     *  designed email at its own width (often 600–700px); in a narrow pane it would dwarf the controls around
     *  it, or run off the side. Omitted → rendered at full size. */
    maxScale?: number
}

const DEFAULT_BODY_STYLE = 'margin:0;padding:20px;background:white;color:black;font-family:Arial,sans-serif;'

/** Behind the sandbox, the second wall for whatever the HTML still asks the browser to fetch or do: nothing
 *  loads except https, data: and cid: images, and no base URL, form target, frame or connection is allowed. */
const previewDocument = (html: string, bodyStyle: string, initialScale?: number): string => `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data: cid:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'; frame-src 'none'; connect-src 'none'">
<meta name="referrer" content="no-referrer">
<meta name="viewport" content="width=device-width,initial-scale=1">
</head>
<body style="${bodyStyle}${initialScale ? `zoom:${initialScale};` : ''}">${html}</body>
</html>`

/**
 * The one sandboxed email preview: email HTML rendered the way a mail client would, isolated from the app. The
 * sandbox never allows scripts, forms or navigation, the content-security policy blocks everything it might
 * fetch but images, and the document keeps the app's stylesheet off the email's own inline styles.
 *
 * Fitting needs the email's natural width, which only the frame's document knows — so a fitted frame adds
 * `allow-same-origin` for the parent to measure it. That is safe only because scripts stay blocked: the
 * content can be read from here but can never act on its own.
 */
export const EmailPreviewFrame = ({ html, title, className, style, bodyStyle = DEFAULT_BODY_STYLE, maxScale }: EmailPreviewFrameProps) => {
    const frameRef = useRef<HTMLIFrameElement>(null)
    // The scale the last fit settled on. Each new html reloads the frame; starting it there instead of at
    // maxScale keeps a narrow pane from jumping on every bid keystroke.
    const fittedScaleRef = useRef(maxScale)

    useEffect(() => {
        const frame = frameRef.current
        if (!frame || maxScale === undefined || typeof ResizeObserver === 'undefined') return
        const fit = () => {
            const document = frame.contentDocument
            if (!document?.body || !frame.clientWidth) return
            document.body.style.zoom = '1'
            const naturalWidth = document.documentElement.scrollWidth
            const scale = Math.min(maxScale, frame.clientWidth / naturalWidth)
            document.body.style.zoom = String(scale)
            fittedScaleRef.current = scale
        }
        // `load` re-fires whenever srcDoc changes, and the observer covers pane resizes.
        frame.addEventListener('load', fit)
        const observer = new ResizeObserver(fit)
        observer.observe(frame)
        return () => {
            frame.removeEventListener('load', fit)
            observer.disconnect()
        }
    }, [maxScale])

    return (
        <iframe
            ref={frameRef}
            title={title}
            sandbox={maxScale === undefined ? '' : 'allow-same-origin'}
            referrerPolicy="no-referrer"
            srcDoc={previewDocument(html, bodyStyle, maxScale === undefined ? undefined : fittedScaleRef.current)}
            className={className}
            style={style}
        />
    )
}
